import dayjs from "dayjs";

import { formatMoney } from "@/lib/format";
import type { CoactiveDebt } from "@/types/domain";

const formatDate = (date: string) => dayjs(date).format("DD/MM/YYYY");

/**
 * Deuda coactiva de la ficha SUNAT de una empresa: el total, cada deuda y cuándo
 * se consultó. Sin dato, avisa que aún no se consultó.
 */
export function CoactiveDebtSection({
  coactiveDebt,
}: {
  coactiveDebt?: CoactiveDebt;
}) {
  return (
    <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 dark:border-slate-800">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
        Deuda coactiva (SUNAT)
      </p>

      {!coactiveDebt ? (
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Aún no consultada.
        </span>
      ) : coactiveDebt.total === 0 ? (
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            Sin deuda en cobranza coactiva
          </span>
          <span className="text-[11px] text-slate-400 dark:text-slate-500">
            Consultado el {formatDate(coactiveDebt.checkedAt)}
          </span>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-red-600 dark:text-red-400">
              {formatMoney(coactiveDebt.total, "S/", true)} en cobranza coactiva ·{" "}
              {coactiveDebt.debts.length}{" "}
              {coactiveDebt.debts.length === 1 ? "deuda" : "deudas"}
            </span>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {coactiveDebt.asOf &&
                `SUNAT la actualizó al ${formatDate(coactiveDebt.asOf)} · `}
              consultado el {formatDate(coactiveDebt.checkedAt)} · no incluye costas
              ni gastos del procedimiento
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                <tr>
                  <th className="py-1.5 pr-4 font-bold">Monto</th>
                  <th className="py-1.5 pr-4 font-bold">Período</th>
                  <th className="py-1.5 pr-4 font-bold">Inicio de cobranza</th>
                  <th className="py-1.5 font-bold">Entidad</th>
                </tr>
              </thead>
              <tbody className="text-slate-600 dark:text-slate-300">
                {coactiveDebt.debts.map((debt, index) => (
                  <tr
                    key={`${debt.period}-${debt.startDate}-${index}`}
                    className="border-t border-slate-100 dark:border-slate-800"
                  >
                    <td className="py-1.5 pr-4 font-semibold">
                      {formatMoney(debt.amount, "S/", true)}
                    </td>
                    <td className="py-1.5 pr-4">{debt.period}</td>
                    <td className="py-1.5 pr-4">{formatDate(debt.startDate)}</td>
                    <td className="py-1.5">{debt.entity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
